import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

/**
 * Central registry for the site's SVG icon library served from the image CDN.
 * Every icon is fetched once and cached — repeated <app-icon> usages of the
 * same name reuse the same request/result instead of re-fetching.
 */
@Injectable({ providedIn: 'root' })
export class IconService {
  private readonly basePath = 'assets/icons/';
  private readonly cache = new Map<string, Observable<SafeHtml>>();

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) {}

  getIcon(name: string): Observable<SafeHtml> {
    if (!this.cache.has(name)) {
      const request$ = this.http
        .get(`${this.basePath}${name}.svg`, { responseType: 'text' })
        .pipe(
          tap((svg) => this.validate(name, svg)),
          catchError(() => of('')),
          shareReplay({ bufferSize: 1, refCount: false })
        );
      this.cache.set(
        name,
        new Observable<SafeHtml>((subscriber) => {
          request$.subscribe((svg) => {
            subscriber.next(this.sanitizer.bypassSecurityTrustHtml(svg));
            subscriber.complete();
          });
        }).pipe(shareReplay({ bufferSize: 1, refCount: false }))
      );
    }
    return this.cache.get(name)!;
  }

  private validate(name: string, svg: string): void {
    // Missing-icon hint for developers only; silent in production
    if (!svg && isDevMode()) {
      console.warn(`[IconService] Icon "${name}" not found at ${this.basePath}${name}.svg`);
    }
  }
}
